import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const INSURER_ID = 'hsbc_smart_care_optimum_enhanced';

const TIERS = [
    { key: 'bronze', label: 'Bronze' },
    { key: 'silver', label: 'Silver' },
    { key: 'gold', label: 'Gold' },
    { key: 'platinum', label: 'Platinum' },
];

// Bronze has no outpatient cover
const OUTPATIENT_TIERS = ['silver', 'gold', 'platinum'];

// The API returns these as `${key}_${tier}`
const OUTPATIENT_MODULES = [
    { key: 'op_general_practitioner', label: 'Outpatient General Practitioner', premiumField: 'op_general_practitioner_premium' },
    { key: 'op_specialist', label: 'Outpatient Specialist', premiumField: 'op_specialist_premium' },
];

// Dental isn't split by tier — the API returns both plans under these keys
const DENTAL_PLANS = [
    { key: 'dental_plan_1', label: 'Plan 1' },
    { key: 'dental_plan_2', label: 'Plan 2' },
];

// Every field the API returns — all are kept so switching tier or dental plan needs no refetch
const RATE_FIELDS = [
    ...TIERS.map(t => t.key),
    ...OUTPATIENT_MODULES.flatMap(m => OUTPATIENT_TIERS.map(tier => `${m.key}_${tier}`)),
    ...DENTAL_PLANS.map(d => d.key),
];

// Only SGD and Annually are offered (the backend enforces the same)
const CURRENCIES = ['SGD'];
const PAYMENT_FREQUENCIES = ['Annually'];
// The rates don't vary by area, so the comparison always shows Singapore
const AREA_OF_COVERAGE = 'Singapore';
const BRAND_COLOR = '#6d0015';
const TIER_ROW_COLOR = '#b9f6ca';
const TOTAL_ROW_COLOR = '#d5d7d8';

function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return 'S$' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as HSBC_SmartCareOptimumEnhanced_Rates: discount/loading scale every benefit
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function HSBC_SmartCareOptimumEnhanced_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const deductibleOptions = appConfig?.content?.deductibles?.[INSURER_ID]?.['SGD'] || [];

    const [tier, setTier] = useState('bronze');
    // Set of selected outpatient module keys
    const [selectedOutpatient, setSelectedOutpatient] = useState(new Set());
    const [dentalSelected, setDentalSelected] = useState(false);
    const [dentalPlan, setDentalPlan] = useState(DENTAL_PLANS[0].key);
    const [currency, setCurrency] = useState('SGD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: string } — deductible per quote
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedTierObj = TIERS.find(t => t.key === tier) || TIERS[0];
    const tierHasOutpatient = OUTPATIENT_TIERS.includes(tier);
    const dentalPlanObj = DENTAL_PLANS.find(d => d.key === dentalPlan) || DENTAL_PLANS[0];

    // Rows shown under the tier's base premium. `field` is the rawRates key for the selected tier.
    const activeBenefits = [
        ...(tierHasOutpatient
            ? OUTPATIENT_MODULES.filter(m => selectedOutpatient.has(m.key)).map(m => ({ ...m, field: `${m.key}_${tier}` }))
            : []),
        ...(dentalSelected
            ? [{ key: 'dental', label: `Dental ${dentalPlanObj.label}`, premiumField: 'dental_premium', field: dentalPlan }]
            : []),
    ];

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            const savedTier = editingPlan.plan || (editingPlan.plan_id || '').replace(`${INSURER_ID}_`, '') || 'bronze';
            const savedDentalPlan = editingPlan.payloads?.dental_plan || DENTAL_PLANS[0].key;
            setTier(savedTier);
            setSelectedOutpatient(new Set(editingPlan.payloads?.outpatient_modules || []));
            setDentalSelected(!!editingPlan.payloads?.dental);
            setDentalPlan(savedDentalPlan);
            setCurrency(editingPlan.payloads?.currency || 'SGD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored per-benefit premiums — only the saved tier's and
            // dental plan's fields are recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return {
                        [savedTier]: unscale(premium?.base_premium ?? null),
                        ...Object.fromEntries(OUTPATIENT_MODULES.map(m => [`${m.key}_${savedTier}`, unscale(premium?.[m.premiumField] ?? null)])),
                        [savedDentalPlan]: unscale(premium?.dental_premium ?? null),
                    };
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open) {
            setTier('bronze');
            setSelectedOutpatient(new Set());
            setDentalSelected(false);
            setDentalPlan(DENTAL_PLANS[0].key);
            setCurrency('SGD');
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
            const defaults = {};
            (quotes || []).forEach(q => {
                defaults[q.name] = deductibleOptions[0] || '';
            });
            setQuoteDeductibles(defaults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Every benefit's rate comes back in one fetch, so toggling a benefit just shows a
    // different set of rows — no refresh needed.
    function toggleOutpatient(key) {
        setSelectedOutpatient(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { [INSURER_ID]: quoteDeductibles[quote.name] || '' },
                            insurers: [INSURER_ID],
                            specifics: {},
                            specific_area_of_coverage: { [INSURER_ID]: '' },
                            rates_version_per_insurer: { [INSURER_ID]: selectedRatesVersion?.[INSURER_ID] || '' },
                            specific_currency: { [INSURER_ID]: currency },
                            specific_payment_frequency: { [INSURER_ID]: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.[INSURER_ID] }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching HSBC SmartCare Optimum Enhanced rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Rate for a rawRates field with discount/loading applied; ratesData keeps the raw API rates
    function getFieldRate(clientIndex, field, quoteUid) {
        const val = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[field];
        if (val === undefined || val === null) return null;
        return typeof val === 'number' ? val * rateFactor : val;
    }

    function getBaseRate(clientIndex, quoteUid) {
        return getFieldRate(clientIndex, tier, quoteUid);
    }

    // Matches computeTotalPremiumPerClient: base plan plus every active benefit
    function getTotal(clientIndex, quoteUid) {
        let total = 0;
        let hasAny = false;
        [tier, ...activeBenefits.map(b => b.field)].forEach(field => {
            const rate = getFieldRate(clientIndex, field, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        });
        return hasAny ? total : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildPlanLabel() {
        if (activeBenefits.length === 0) return selectedTierObj.label;
        return `${selectedTierObj.label} + ${activeBenefits.map(b => b.label).join(' + ')}`;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const planId = `${INSURER_ID}_${tier}`;
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const activeOutpatientKeys = tierHasOutpatient ? [...selectedOutpatient] : [];
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const premium = {
                    insurer: INSURER_ID,
                    plan: tier,
                    plan_label: planLabel,
                    included_benefits: '',
                    deductible: quoteDeductibles[quote.name] || '',
                    area_of_coverage: AREA_OF_COVERAGE,
                    currency,
                    payment_frequency: paymentFrequency,
                    discount,
                    loading: loadingPercent,
                    premium_rate: getTotal(clientIndex, quote.uid) ?? null,
                    // Stored with discount/loading applied; unselected benefits are null
                    base_premium: getBaseRate(clientIndex, quote.uid),
                    dental_premium: dentalSelected ? getFieldRate(clientIndex, dentalPlan, quote.uid) : null,
                };

                OUTPATIENT_MODULES.forEach(m => {
                    premium[m.premiumField] = activeOutpatientKeys.includes(m.key)
                        ? getFieldRate(clientIndex, `${m.key}_${tier}`, quote.uid)
                        : null;
                });

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId,
                    planUid,
                    premium,
                });
            });
        });

        const plan = {
            plan_id: planId,
            uid: planUid,
            plan: tier,
            insurer: INSURER_ID,
            label: `HSBC SmartCare Optimum Enhanced ${planLabel}`,
            payloads: {
                area_of_coverage: AREA_OF_COVERAGE,
                currency,
                payment_frequency: paymentFrequency,
                outpatient_modules: activeOutpatientKeys,
                dental: dentalSelected,
                dental_plan: dentalPlan,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                }}>SmartCare Optimum Enhanced</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 1.5, py: 0.5 }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/hsbc-life-logo.svg`}
                            alt="HSBC Life"
                            style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={tier}
                            disabled={loading || !!editingPlan}
                            // Rates for every tier come back in one fetch, so switching tiers just shows different rows
                            onChange={(e) => setTier(e.target.value)}
                            sx={selectSx}
                        >
                            {TIERS.map(t => (
                                <MenuItem key={t.key} value={t.key} sx={menuItemSx}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Benefits — outpatient isn't offered on Bronze; dental has its own plan sub-option */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Benefits</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {OUTPATIENT_MODULES.map(module => (
                            <FormControlLabel
                                key={module.key}
                                sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={tierHasOutpatient && selectedOutpatient.has(module.key)}
                                        onChange={() => toggleOutpatient(module.key)}
                                        disabled={!tierHasOutpatient || loading || !!editingPlan}
                                    />
                                }
                                label={module.label}
                            />
                        ))}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13, width: 55 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={dentalSelected}
                                        onChange={() => setDentalSelected(prev => !prev)}
                                        disabled={loading || !!editingPlan}
                                    />
                                }
                                label="Dental"
                            />
                            <Select
                                size="small"
                                value={dentalPlan}
                                // Both dental plans come back in one fetch, so switching just shows a different rate
                                onChange={(e) => setDentalPlan(e.target.value)}
                                sx={{ ...selectSx, width: 90 }}
                                disabled={loading || !!editingPlan}
                            >
                                {DENTAL_PLANS.map(d => (
                                    <MenuItem key={d.key} value={d.key} sx={menuItemSx}>{d.label}</MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={currency} disabled={loading} onChange={(e) => { setCurrency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Deductible per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductible Option</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                    isZeroDeductibleUniform={false}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Tier row carries the base plan premium — same as HSBC_SmartCareOptimumEnhanced_Rates */}
                                            <TableRow sx={{ backgroundColor: TIER_ROW_COLOR }}>
                                                <TableCell sx={{ fontWeight: 600, color: '#000' }}>{selectedTierObj.label}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#000' }}>
                                                        {formatRate(getBaseRate(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 500, color: '#000' }}>
                                                    {formatRate(sumClients(i => getBaseRate(i, quote.uid)))}
                                                </TableCell>
                                            </TableRow>
                                            {activeBenefits.map((benefit, benefitIdx) => (
                                                <TableRow key={benefit.key} sx={{ backgroundColor: benefitIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{benefit.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getFieldRate(idx, benefit.field, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(sumClients(i => getFieldRate(i, benefit.field, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: TOTAL_ROW_COLOR }}>
                                                <TableCell sx={{ fontWeight: 700 }}>Total Premium</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                        {formatRate(getTotal(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    {formatRate(sumClients(i => getTotal(i, quote.uid)))}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId={INSURER_ID} />

                <RatesVersionSelect
                    insurer={INSURER_ID}
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
